import api from './api'
import type { User, PaginatedResponse } from '@/types'
import type {
  ApiResponse,
  InviteUserRequest,
  UpdateUserPreferencesRequest,
  UpdateUserRequest,
} from '@/types/api'

const UsersService = {
  async getAll(params?: {
    search?: string
    role?: string
    is_active?: boolean
    page?: number
    per_page?: number
  }): Promise<PaginatedResponse<User>> {
    const response = await api.get<PaginatedResponse<User>>('/api/v1/users', {
      params,
    })
    return response.data
  },

  async getById(userId: string): Promise<User> {
    const response = await api.get<ApiResponse<User>>(`/api/v1/users/${userId}`)
    return response.data.data
  },

  async invite(data: InviteUserRequest): Promise<User> {
    const response = await api.post<ApiResponse<User>>('/api/v1/users/invite', data)
    return response.data.data
  },

  async update(userId: string, data: UpdateUserRequest): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/api/v1/users/${userId}`, data)
    return response.data.data
  },

  async updateRole(userId: string, role: string): Promise<User> {
    const response = await api.put<ApiResponse<User>>(`/api/v1/users/${userId}/role`, {
      role,
    })
    return response.data.data
  },

  async deactivate(userId: string): Promise<void> {
    await api.put(`/api/v1/users/${userId}`, { is_active: false })
  },

  async activate(userId: string): Promise<void> {
    await api.put(`/api/v1/users/${userId}`, { is_active: true })
  },

  async updateProfile(data: {
    full_name?: string
    avatar_url?: string
    native_notifications_enabled?: boolean
  }): Promise<User> {
    const response = await api.put<ApiResponse<User>>('/api/v1/users/me', data)
    return response.data.data
  },

  async updatePreferences(data: UpdateUserPreferencesRequest): Promise<User> {
    const response = await api.put<ApiResponse<User>>('/api/v1/users/me/preferences', data)
    return response.data.data
  },

  async uploadAvatar(file: File): Promise<User> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post<ApiResponse<User>>('/api/v1/users/me/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data.data
  },
}

export default UsersService
